import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config/constants";

export const VerificationError: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string>("");

  const participantToken = searchParams.get("participantToken");
  const errorMessage =
    searchParams.get("error") ||
    "Your verification link has expired or is invalid.";

  const handleResendVerification = async () => {
    if (!participantToken) {
      setResendError("Cannot resend verification. Participant token is missing.");
      return;
    }

    setIsResending(true);
    setResendError("");

    try {
      const response = await fetch(
        `${API_BASE_URL}/participants/resend-verification`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            participantToken,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        setResendError(
          data.message || "Failed to resend verification email. Please try again."
        );
        return;
      }

      setResendSuccess(true);
    } catch (err) {
      setResendError("Network error. Please check your connection and try again.");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full rounded-lg bg-white p-8 shadow-lg">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100">
          <svg
            className="h-8 w-8 text-yellow-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        <h2 className="mb-2 text-center text-xl font-semibold text-gray-900">
          Verification Link Expired
        </h2>

        <p className="mb-6 text-center text-gray-600">{errorMessage}</p>

        {resendSuccess ? (
          <div className="rounded-lg bg-green-50 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Verification email sent successfully! Please check your inbox.
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {resendError && (
          <div className="rounded-lg bg-red-50 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">
                  {resendError}
                </p>
              </div>
            </div>
          </div>
        )}

        {participantToken && !resendSuccess && (
          <button
            onClick={handleResendVerification}
            disabled={isResending}
            className="w-full rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600 disabled:bg-gray-400 disabled:cursor-not-allowed mb-3"
          >
            {isResending ? "Sending..." : "Request New Verification Link"}
          </button>
        )}

        <button
          onClick={() => navigate("/")}
          className="w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
        >
          Return to Home
        </button>

        {!participantToken && (
          <p className="mt-4 text-center text-sm text-gray-500">
            If you need assistance, please contact support.
          </p>
        )}
      </div>
    </div>
  );
};
